import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';

import { selectUnits } from 'shared/slice/unitsSlice';
import { flexBoxCenter, radiusSection } from 'shared/styles/commonStyles';

import SwitchMasterControl from './SwitchMasterControl';
import ZoneDetailPanel from './ZoneDetailPanel';
import { useLocations } from 'shared/hooks';

const STATUS_DOT = {
  fault: { bg: '#ef4444', shadow: '0 0 8px #ef4444' },
  online: { bg: '#22c55e', shadow: '0 0 8px #4ade80' },
  offline: { bg: 'var(--switch-control-track)', shadow: 'none' },
};

const getSubLocationStatus = (flatSwitch, specId) => {
  const machines = flatSwitch[specId] || {};
  if (Object.values(machines).some((m) => m.isFaults)) return 'fault';
  if (Object.values(machines).some((m) => m.isWifi)) return 'online';
  return 'offline';
};

const LocationPanel = ({
  swtName,
  switchData,
  selectedLocation,
  selectedSpecificLocation,
  selectedMachine,
  flatSwitch,
  onSelectMachine,
  onClearMachine,
  onSelectSpecificLocation = () => {},
  onClearSpecificLocation = null,
}) => {
  const isMobile = useMediaQuery({ query: '(max-width:600px)' });
  const locations = useLocations();
  const { isF } = useSelector(selectUnits);

  if (!selectedLocation) {
    return (
      <PlaceholderWrapper>
        <PlaceholderOrb>
          <PlaceholderIcon />
        </PlaceholderOrb>
        <PlaceholderTitle>Pick a zone to open the live overview</PlaceholderTitle>
        <PlaceholderText>
          The location snapshot appears here first. Select one of its monitored switches
          to continue into telemetry and active program controls.
        </PlaceholderText>
      </PlaceholderWrapper>
    );
  }

  const isSpecificLocation = switchData[selectedLocation]?.isSpecificLocation;
  const effectiveLocationId = selectedSpecificLocation || selectedLocation;
  const locData = locations?.[swtName]?.[effectiveLocationId] || {};

  if (isSpecificLocation && !selectedSpecificLocation) {
    const parentLocData = locations?.[swtName]?.[selectedLocation] || {};
    const parentLabel =
      parentLocData.location_name || parentLocData.locationName || selectedLocation;
    const subLocations = switchData[selectedLocation]?.subLocations || {};

    return (
      <SubLocationWrapper>
        <SubLocationHeader>
          <SubLocationTitle>{parentLabel}</SubLocationTitle>
          <SubLocationCount>{Object.keys(subLocations).length} sub-locations</SubLocationCount>
        </SubLocationHeader>
        <SubLocationGrid>
          {Object.keys(subLocations).map((specId) => {
            const specData = locations?.[swtName]?.[specId] || {};
            const label =
              specData.location_name ||
              specData.locationName ||
              specData.specific_address ||
              specId;
            const shortName = specData.location_name_short || specData.zone_name_short || '';
            const specificAddress = specData.specific_address || specData.locationAddress || '';
            const company = specData.company_name || specData.companyName || '';
            const address = [specData.zone_city, specData.zone_state_short_name]
              .filter(Boolean)
              .join(', ');
            const status = getSubLocationStatus(flatSwitch, specId);
            const deviceCount = Object.keys(subLocations[specId]?.devices || {}).length;

            return (
              <SubLocationCard
                key={specId}
                type="button"
                onClick={() => onSelectSpecificLocation(specId)}
              >
                <SubLocTopRow>
                  <SubLocNameGroup>
                    <SubLocStatusDot $status={status} />
                     {specificAddress && <SubLocName>{specificAddress}</SubLocName>}
                  </SubLocNameGroup>
                  <SubLocBadge $hasFault={status === 'fault'}>
                    {deviceCount} switches
                  </SubLocBadge>
                </SubLocTopRow>
                {(shortName || specificAddress || company || address) && (
                  <SubLocInfoRow>
                    {shortName && <SubLocAddress>{shortName}</SubLocAddress>}
                    <SubLocAddress>{label}</SubLocAddress>
                    {company && <SubLocCompany>{company}</SubLocCompany>}
                    {address && <SubLocAddress>{address}</SubLocAddress>}
                  </SubLocInfoRow>
                )}
              </SubLocationCard>
            );
          })}
        </SubLocationGrid>
      </SubLocationWrapper>
    );
  }

  let machines = [];
  if (isSpecificLocation && selectedSpecificLocation) {
    machines = Object.keys(
      switchData[selectedLocation]?.subLocations?.[selectedSpecificLocation]?.devices || {}
    );
  } else if (!isSpecificLocation) {
    machines = Object.keys(switchData[selectedLocation]?.devices || {});
  }

  if (selectedMachine) {
    return (
      <DetailWrapper>
        <SwitchMasterControl
          swtName={swtName}
          location={effectiveLocationId}
          machine={selectedMachine}
          isMobile={isMobile}
          onClose={onClearMachine}
        />
      </DetailWrapper>
    );
  }

  return (
    <>
      <ZoneDetailPanel
        swtName={swtName}
        locationId={effectiveLocationId}
        locationData={locData}
        machineIds={machines}
        machineMap={flatSwitch?.[effectiveLocationId] || {}}
        isF={isF}
        onSelectMachine={onSelectMachine}
        onBack={isSpecificLocation && selectedSpecificLocation ? onClearSpecificLocation : null}
      />
    </>
  );
};

export default LocationPanel;

const DetailWrapper = styled.div`
  flex: 1;
  display: flex;
  min-width: 0;
  width: 100%;
`;

const PlaceholderWrapper = styled.div`
  flex: 1;
  ${flexBoxCenter};
  flex-direction: column;
  gap: 16px;
  padding: 40px 32px;
  margin: 12px;
  border-radius: ${radiusSection};
  border: 1px dashed rgba(59, 130, 246, 0.24);
  background: var(--switch-panel-bg-soft);
  text-align: center;
`;

const PlaceholderOrb = styled.span`
  width: 68px;
  height: 68px;
  border-radius: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--switch-blue-soft);
  border: 1px solid rgba(59, 130, 246, 0.22);
  box-shadow: var(--switch-shadow-soft);
`;

const PlaceholderIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <rect x="3.75" y="3.75" width="20.5" height="20.5" rx="6.25" stroke="#60A5FA" strokeWidth="1.5" />
    <path
      d="M10 14h8M14 10v8"
      stroke="#93C5FD"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PlaceholderTitle = styled.h3`
  margin: 0;
  color: var(--switch-text-primary);
  font-size: 22px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.6px;
`;

const PlaceholderText = styled.p`
  max-width: 420px;
  margin: 0;
  color: var(--switch-text-muted);
  font-size: 13px;
  line-height: 1.7;
`;

const SubLocationWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 16px;
  min-width: 0;
  width: 100%;
`;

const SubLocationHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 0 4px;
`;

const SubLocationTitle = styled.h3`
  margin: 0;
  color: var(--switch-text-secondary);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.4px;
`;

const SubLocationCount = styled.span`
  color: var(--switch-text-muted);
  font-size: 12px;
  font-weight: 500;
`;

const SubLocationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 10px;
`;

const SubLocationCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 18px;
  border-radius: 14px;
  border: 0.8px solid var(--switch-border);
  background: var(--switch-card-bg);
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--switch-panel-bg-alt);
    border-color: var(--switch-blue-border);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.15);
  }
`;

const SubLocTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const SubLocNameGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const SubLocStatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $status }) => STATUS_DOT[$status]?.bg || STATUS_DOT.offline.bg};
  box-shadow: ${({ $status }) => STATUS_DOT[$status]?.shadow || STATUS_DOT.offline.shadow};
`;

const SubLocName = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: var(--switch-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubLocBadge = styled.span`
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ $hasFault }) => ($hasFault ? 'var(--switch-danger-soft)' : 'var(--switch-panel-bg-alt)')};
  color: ${({ $hasFault }) => ($hasFault ? '#ef4444' : 'var(--switch-text-faint)')};
`;

const SubLocInfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 14px;
`;

const SubLocCompany = styled.span`
  font-size: 11px;
  color: var(--switch-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubLocAddress = styled.span`
  font-size: 10px;
  color: var(--switch-text-faint);
`;
